import { getModule as getStoreModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Action } from '@libraries/vuex-module-decorators/Action';
import ContactService from '@app/services/ContactService';
import IClientLogo from '@app/types/base/IClientLogo';
import ClientLogoService from '@app/services/ClientLogoService';
import LocaleService from '@app/services/LocaleService';
import ILocale from '@app/types/base/ILocale';
import LocationService from '@app/services/LocationService';
import RegionService from '@app/services/RegionService';
import IRegion from '@app/types/base/IRegion';
import TrainService from '@app/services/TrainService';
import ITrain from '@app/types/base/ITrain';
import IFlatConnection from '@app/types/data/base/IFlatConnection';
import ConnectionService from '@app/services/ConnectionService';

import componentsConfig from '@app/components/components.config.js';
import ScheduledActionsService from '@app/services/presentations/ScheduledActionsService';
import I18nService from '@app/services/I18nService';
import IRawContact from '@/app/src/types/base/IRawContact';
import StateCacheService from '@/app/src/services/cache/StateCacheService';
import IPublishingEvent from '@app/types/base/IPublishingEvent';
import PublishingEventType from '@app/types/base/PublishingEventType';
import {
	throwCacheErrorIfUnsupportedInClient,
	throwIfNotCachingUnsupportedInClient
} from '@app/types/errors/CacheError';
import OfflineError, { isNetworkOrOfflineErrorAndAppIsOffline } from '@app/types/errors/OfflineError';
import AuthenticationError from '../../types/errors/AuthenticationError';
import store from '../store';
import AppStoreModule from './AppStoreModule';
import ILocation from '../../types/base/ILocation';
import AuthStoreModule from './AuthStoreModule';


const appStore: AppStoreModule = getStoreModule(AppStoreModule);
const authStore: AuthStoreModule = getStoreModule(AuthStoreModule);

const moduleId = 'globals';

@Module({
	name: moduleId,
	store,
	dynamic: true,
	namespaced: true
})
export default class GlobalsStoreModule extends VuexModule {
	public contactsByLocaleCode: Record<string, IRawContact[]> = {};

	public locationsByLocaleCode: Record<string, ILocation[]> = {};

	public trainsByLocaleCode: Record<string, ITrain[]> = {};

	public regionsByLocaleCode: Record<string, IRegion[]> = {};

	public connectionsByLocaleCode: Record<string, IFlatConnection[]> = {};

	public clientLogos: IClientLogo[] = [];

	public locales: ILocale[] = [];

	public loadingPromises: Record<string, Promise<any> | undefined> = {};

	public i18nTokensByLocaleCode: Record<string, Record<string, string>> = {
		[process.env.PRESENTATION_DEFAULT_LOCALE as string]: {}
	};

	public entitiesDateUpdated: number = 0;

	/**
	 * Dictionary of ids that are known to expire at some point in time.
	 * Please note: this may include arbitrary ids from all kinds of entities,
	 * even though the application currently only handles expiration of slides
	 */
	public scheduledExpirationDatesById: Record<string, IPublishingEvent[]> = {};

	@Action
	public async saveState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const globalsStateCacheService: StateCacheService = new StateCacheService('globals');

		await globalsStateCacheService.save('contacts', this.contactsByLocaleCode);
		await globalsStateCacheService.save('locations', this.locationsByLocaleCode);
		await globalsStateCacheService.save('regions', this.regionsByLocaleCode);
		await globalsStateCacheService.save('connections', this.connectionsByLocaleCode);
		await globalsStateCacheService.save('clientLogos', this.clientLogos);
		await globalsStateCacheService.save('locales', this.locales);
		await globalsStateCacheService.save('trains', this.trainsByLocaleCode);
		await globalsStateCacheService.save('i18n', this.i18nTokensByLocaleCode);
		await globalsStateCacheService.save('scheduledExpirationDatesById', this.scheduledExpirationDatesById);
	}

	@Action
	public async restoreState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const cache: StateCacheService = new StateCacheService();

		const globalsStateCacheService: StateCacheService = new StateCacheService('globals');
		const globalsStateCacheKeys: string[] = await globalsStateCacheService.keys();

		if (globalsStateCacheKeys.length > 0) {
			this.setContacts((await globalsStateCacheService.restore('contacts')) || this.contactsByLocaleCode);
			this.setLocations((await globalsStateCacheService.restore('locations')) || this.locationsByLocaleCode);
			this.setRegions((await globalsStateCacheService.restore('regions')) || this.regionsByLocaleCode);
			this.setConnections((await globalsStateCacheService.restore('connections')) || this.connectionsByLocaleCode);
			this.setClientLogos((await globalsStateCacheService.restore('clientLogos')) || this.clientLogos);
			this.setLocales((await globalsStateCacheService.restore('locales')) || this.locales);
			this.setTrains((await globalsStateCacheService.restore('trains')) || this.trainsByLocaleCode);
			this.setI18nTokens((await globalsStateCacheService.restore('i18n')) || this.i18nTokensByLocaleCode);
			this.setScheduledExpirationDatesById(
				(await globalsStateCacheService.restore('scheduledExpirationDatesById')) || this.scheduledExpirationDatesById
			);

			this.acknowledgeUpdatedEntities();

			return;
		}

		// Legacy support for all store modules stored in a single table

		const cachedState: Record<string, any> | null = await cache.restore(moduleId);
		if (!cachedState) return;

		if (cachedState.contactsByLocaleCode) this.setContacts(cachedState.contactsByLocaleCode);
		if (cachedState.locationsByLocaleCode) this.setLocations(cachedState.locationsByLocaleCode);
		if (cachedState.regionsByLocaleCode) this.setRegions(cachedState.regionsByLocaleCode);
		if (cachedState.connectionsByLocaleCode) this.setConnections(cachedState.connectionsByLocaleCode);
		if (cachedState.clientLogos) this.setClientLogos(cachedState.clientLogos);
		if (cachedState.locales) this.setLocales(cachedState.locales);
		if (cachedState.trainsByLocaleCode) this.setTrains(cachedState.trainsByLocaleCode);
		if (cachedState.i18nByLocaleCode) this.setI18nTokens(cachedState.i18nByLocaleCode);

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async saveStateIfSupported(): Promise<void> {
		return this.saveState().catch(throwIfNotCachingUnsupportedInClient);
	}

	@Action
	public async restoreStateIfSupported(): Promise<void> {
		return this.restoreState().catch(throwIfNotCachingUnsupportedInClient);
	}

	@Action
	public async loadAll(): Promise<void> {
		await Promise.all([
			this.loadI18n(),
			this.loadContacts(),
			this.loadLocations(),
			this.loadRegions(),
			this.loadConnections(),
			this.loadTrains(),
			this.loadClientLogos(),
			this.loadLocales(),
			this.loadExpirationDates().catch((error: Error): void => {
				if (error instanceof AuthenticationError) {
					console.info('[GlobalsStore] Skipped loading expiration dates as user is unauthenticated.');
				} else {
					throw error;
				}
			})
		]);
	}

	@Action
	public async loadContacts(): Promise<void> {
		await this.loadLocales();

		if (Object.keys(this.contactsByLocaleCode).length > 0 && !appStore.isOnline) {
			return;
		}
		try {
			const contactService: ContactService = new ContactService(this.locales);
			const contactsByLocaleCode: Record<string, IRawContact[]> = await contactService.getContacts();

			this.setContacts(contactsByLocaleCode);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load contacts as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadI18n(): Promise<void> {
		await this.loadLocales();

		if (
			(Object.keys(this.i18nTokensByLocaleCode[process.env.PRESENTATION_DEFAULT_LOCALE as string]).length > 0 ||
				this.loadingPromises.i18n) &&
			!appStore.isOnline
		)
			return this.loadingPromises.i18n;

		try {
			const i18nService: I18nService = new I18nService();
			this.loadingPromises.i18n = i18nService.getI18n();

			const i18nDictionary: Record<string, Record<string, string>> = await this.loadingPromises.i18n;

			// inject component config tokens, but highlight them  as not bein loaded from real i18n source
			const componentsConfigI18n: Record<string, any> = componentsConfig.context.i18n as Record<string, any>;
			Object.keys(componentsConfigI18n).forEach((key: string): void => {
				if (!i18nDictionary[process.env.PRESENTATION_DEFAULT_LOCALE as string][key]) {
					i18nDictionary[process.env.PRESENTATION_DEFAULT_LOCALE as string][key] = `@${componentsConfigI18n[key]}@`;
				}
			});

			// add missing locales that have a fallback locale configured
			let missingLocalesWithFallback: ILocale[] = this.locales.filter(
				(locale: ILocale): boolean => !i18nDictionary[locale.code] && locale.fallbackCode
			);

			// protect against endless loop if locales have fallbackCodes targeting empty locales with no fallback themselves
			let numIterations = 0;
			do {
				missingLocalesWithFallback = missingLocalesWithFallback.filter((locale: ILocale): boolean => {
					// keep in array if fallback locale has not been pupulated yet
					if (!i18nDictionary[locale.fallbackCode as string]) return true;

					// populate with fallback locale
					i18nDictionary[locale.code] = i18nDictionary[locale.fallbackCode as string];
					return false;
				});

				numIterations++;
			} while (missingLocalesWithFallback.length > 0 && numIterations < this.locales.length);

			this.setI18nTokens(i18nDictionary);
		} catch (error) {
			if (!isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load i18n as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadLocations(): Promise<void> {
		await this.loadLocales();

		if ((Object.keys(this.locationsByLocaleCode).length > 0 || this.loadingPromises.locations) && !appStore.isOnline) {
			return this.loadingPromises.locations;
		}

		try {
			const locationService: LocationService = new LocationService(this.locales);
			this.loadingPromises.locations = locationService.getLocations();
			const locationsByLocaleCode: Record<string, ILocation[]> = await this.loadingPromises.locations;

			this.setLocations(locationsByLocaleCode);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load locations as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadRegions(): Promise<void> {
		await this.loadLocales();

		if ((Object.keys(this.regionsByLocaleCode).length > 0 || this.loadingPromises.regions) && !appStore.isOnline) {
			return this.loadingPromises.regions;
		}

		try {
			const regionService: RegionService = new RegionService(this.locales);
			this.loadingPromises.regions = regionService.getRegions();
			const regionsByLocaleCode: Record<string, IRegion[]> = await this.loadingPromises.regions;

			this.setRegions(regionsByLocaleCode);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load regions as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadConnections(): Promise<void> {
		await this.loadLocales();

		if (
			(Object.keys(this.connectionsByLocaleCode).length > 0 || this.loadingPromises.connections) &&
			!appStore.isOnline
		) {
			return this.loadingPromises.connections;
		}
		try {
			const connectionService: ConnectionService = new ConnectionService(this.locales);
			this.loadingPromises.connections = connectionService.getFlatConnections();
			const connectionsByLocaleCode: Record<string, IFlatConnection[]> = await this.loadingPromises.connections;

			this.setConnections(connectionsByLocaleCode);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load connections as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadTrains(): Promise<void> {
		await this.loadLocales();

		if ((Object.keys(this.trainsByLocaleCode).length > 0 || this.loadingPromises.trains) && !appStore.isOnline) {
			return this.loadingPromises.trains;
		}

		try {
			const trainService: TrainService = new TrainService(this.locales);
			this.loadingPromises.trains = trainService.getTrains();
			const trainsByLocaleCode: Record<string, ITrain[]> = await this.loadingPromises.trains;

			this.setTrains(trainsByLocaleCode);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load trains as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadClientLogos(): Promise<void> {
		await this.loadLocales();

		if ((this.clientLogos.length > 0 || this.loadingPromises.clientLogos) && !appStore.isOnline) {
			return this.loadingPromises.clientLogos;
		}

		try {
			const clientLogoService: ClientLogoService = new ClientLogoService();
			this.loadingPromises.clientLogos = clientLogoService.getLogos();
			const clientLogos: IClientLogo[] = await this.loadingPromises.clientLogos;

			this.setClientLogos(clientLogos);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load clientLogos as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadLocales(): Promise<void> {
		// If the app is offline, return locales existing in store
		if (this.locales.length > 0 && !appStore.isOnline) {
			return;
		}

		// If new locales are already being loaded, return the loading promise
		// Please note: The "loadLocales" method is called several times, but should only be loaded once
		if (this.loadingPromises.locales) {
			return this.loadingPromises.locales;
		}

		try {
			const localeService: LocaleService = new LocaleService();
			this.loadingPromises.locales = localeService.getLocales();
			const locales: ILocale[] = await this.loadingPromises.locales;

			this.setLocales(locales);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load locales as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Action
	public async loadExpirationDates(): Promise<void> {
		if (!authStore.isLoggedIn) {
			throw new AuthenticationError('Cannot load expiration dates as current user is not authenticated');
		}

		if (
			(Object.keys(this.scheduledExpirationDatesById).length > 0 || this.loadingPromises.expirationDates) &&
			!appStore.isOnline
		) {
			return this.loadingPromises.expirationDates;
		}

		try {
			const scheduledActionsService: ScheduledActionsService = new ScheduledActionsService();
			this.loadingPromises.expirationDates = scheduledActionsService.getScheduledPublishingEvents();
			this.setScheduledExpirationDatesById(await this.loadingPromises.expirationDates);
		} catch (error) {
			if (isNetworkOrOfflineErrorAndAppIsOffline(error)) {
				throw new OfflineError('Cannot load expirationDates as application is offline');
			} else {
				throw error;
			}
		}

		this.acknowledgeUpdatedEntities();
	}

	@Mutation
	protected setContacts(contactsByLocaleCode: Record<string, IRawContact[]>): void {
		this.contactsByLocaleCode = contactsByLocaleCode;
	}

	@Mutation
	protected setI18nTokens(i18nTokensByLocaleCode: Record<string, Record<string, string>>): void {
		this.i18nTokensByLocaleCode = i18nTokensByLocaleCode;
	}

	@Mutation
	public acknowledgeUpdatedEntities(): void {
		this.entitiesDateUpdated = Date.now();
	}

	@Mutation
	protected setLocations(locationsByLocaleCode: Record<string, ILocation[]>): void {
		this.locationsByLocaleCode = locationsByLocaleCode;
	}

	@Mutation
	protected setTrains(trainsByLocaleCode: Record<string, ITrain[]>): void {
		this.trainsByLocaleCode = trainsByLocaleCode;
	}

	@Mutation
	protected setRegions(regionsByLocaleCode: Record<string, IRegion[]>): void {
		this.regionsByLocaleCode = regionsByLocaleCode;
	}

	@Mutation
	protected setConnections(connectionsByLocaleCode: Record<string, IFlatConnection[]>): void {
		this.connectionsByLocaleCode = connectionsByLocaleCode;
	}

	@Mutation
	protected setClientLogos(clientLogos: IClientLogo[]): void {
		this.clientLogos = clientLogos;
	}

	@Mutation
	protected setLocales(locales: ILocale[]): void {
		this.locales = locales;
	}

	@Mutation
	protected setScheduledExpirationDatesById(expirationDatesById: Record<string, IPublishingEvent[]>): void {
		this.scheduledExpirationDatesById = expirationDatesById;
	}

	public get contactsById(): Record<string, IRawContact> {
		const contactsById: Record<string, IRawContact> = {};

		this.contacts.forEach((contact: IRawContact): void => {
			contactsById[contact.id] = contact;
		});

		return contactsById;
	}

	public get i18n(): Record<string, string> {
		// If no loading was started (=fractal context), return the full i18n from components.config.js
		if (!Object.values(this.i18nTokensByLocaleCode[appStore.localeCode]).length) {
			return componentsConfig.context.i18n as Record<string, any>;
		}

		let i18n = {};
		// Please note: English has a fallback for DE, but all other languages have a fallback for English.
		// So if no English value is set, the other languages would have an empty value (e.g. for distanceWithUnit, which needs no localization)
		if (this.i18nTokensByLocaleCode.de) {
			i18n = {
				...this.i18nTokensByLocaleCode.de
			};
		}

		if (this.i18nTokensByLocaleCode[appStore.localeCode]) {
			i18n = {
				...i18n,
				...this.i18nTokensByLocaleCode[appStore.localeCode]
			};
		}

		return i18n;
	}

	public get contacts(): IRawContact[] {
		return this.contactsByLocaleCode[appStore.localeCode] || [];
	}

	public get locations(): ILocation[] {
		return this.locationsByLocaleCode[appStore.localeCode] || [];
	}

	public get trains(): ITrain[] {
		return this.trainsByLocaleCode[appStore.localeCode] || [];
	}

	public get regions(): IRegion[] {
		return this.regionsByLocaleCode[appStore.localeCode] || [];
	}

	public get connections(): IFlatConnection[] {
		return this.connectionsByLocaleCode[appStore.localeCode] || [];
	}

	/**
	 * Retrieves a map with all ids and their last action in the past. ids with no actions in the past are omitted
	 */
	public get lastExecutedScheduledActionById(): Record<string, IPublishingEvent> {
		const now: number = Date.now();

		const lastScheduledActionById: Record<string, IPublishingEvent> = {};

		Object.keys(this.scheduledExpirationDatesById).forEach((id: string): void => {
			// identify the event in the past that was executed last
			const lastExecutedScheduledEvent: IPublishingEvent | undefined = this.scheduledExpirationDatesById[id]
				.reverse()
				.find((event: IPublishingEvent): boolean => new Date(event.dateTime).getTime() < now);

			// all scheduled events are in the future
			if (!lastExecutedScheduledEvent) return;

			lastScheduledActionById[id] = lastExecutedScheduledEvent;
		});

		return lastScheduledActionById;
	}

	/**
	 * Retrieves all ids that are currently unpublished as per schedule
	 */
	public get currentlyUnpublishedIdsAsPerSchedule(): string[] {
		// the id is published as per schedule if the last event was an unpublish event,
		return Object.keys(this.lastExecutedScheduledActionById).filter(
			(id: string): boolean => this.lastExecutedScheduledActionById[id].type === PublishingEventType.UnPublish
		);
	}
}
